import { useEffect, useRef, useState, useCallback } from 'react'
import { getSocket, disconnectSocket } from '../lib/socket'
import { postToParent } from '../lib/token'
import type {
  RoundState, PlacedBet, SettledBet,
  DealResult, RoundResult, GameType,
} from '../lib/types'

type ConnStatus = 'connecting' | 'connected' | 'disconnected' | 'error'

const INITIAL_ROUND: RoundState = {
  roundId: '', status: 'idle', seedHash: '',
  bettingCloses: 0, myBets: [], winAmount: 0,
}

export function useGameSocket(token: string, playerId: string, gameType: string) {
  const [connStatus, setConnStatus] = useState<ConnStatus>('connecting')
  const [round,      setRound]      = useState<RoundState>(INITIAL_ROUND)
  const [balance,    setBalance]    = useState<number | null>(null)
  const [error,      setError]      = useState<string | null>(null)
  const [activity,   setActivity]   = useState<string[]>([])   // recent bet activity log
const [initialResults, setInitialResults] = useState<string[]>([])

  const socketRef = useRef(getSocket(token))

  const addActivity = (msg: string) =>
    setActivity(prev => [msg, ...prev].slice(0, 6))

  useEffect(() => {
    const socket = socketRef.current

    socket.on('connect', () => {
      setConnStatus('connected')
      setError(null)
    })

    socket.on('connect_error', (err) => {
      setConnStatus('error')
      setError(err.message)
    })

    socket.on('disconnect', () => setConnStatus('disconnected'))

    // ── Server events ────────────────────────────────────────
socket.on('connected', async (data) => {
  postToParent('GAME_CONNECTED', { player_id: data.player_id })
  try {
    const GAME_SERVER = import.meta.env.VITE_GAME_SERVER_URL || 'http://localhost:3002'
    // Fetch balance
    const r = await fetch(`${GAME_SERVER}/wallet/balance?player_id=${data.player_id}&token=${token}`)
    const d = await r.json()
    if (d.balance !== undefined) setBalance(d.balance)
    // Fetch last results - pass to App via callback
    const r2 = await fetch(`${GAME_SERVER}/results/${gameType}`)
    const d2 = await r2.json()
    if (d2.results) setInitialResults(d2.results)
  } catch {}
})

    socket.on('round:start', ({ round_id, seed_hash, betting_closes }) => {
      setRound({
        roundId:       round_id,
        status:        'betting',
        seedHash:      seed_hash,
        bettingCloses: betting_closes,
        myBets:        [],
        winAmount:     0,
      })
      setActivity([])
    })

    socket.on('round:closed', () => {
      setRound(prev => ({ ...prev, status: 'closed' }))
    })

    socket.on('round:result', ({ round_id, deal, result, seed, settled }) => {
      // Find my settlements
      const mySettled = settled.filter((s: SettledBet) => s.player_id === playerId)
      const winTotal  = mySettled.reduce((sum: number, s: SettledBet) => sum + (s.payout ?? 0), 0)

      setRound(prev => ({
        ...prev,
        status:    'result',
        deal,
        result,
        seed,
        winAmount: winTotal,
        myBets:    prev.myBets.map(b => {
          const settled = mySettled.find((s: SettledBet) => s.bet_type === b.betType)
          return settled
            ? { ...b, outcome: settled.outcome, payout: settled.payout }
            : b
        }),
      }))

      if (winTotal > 0) {
        setBalance(prev => (prev !== null ? prev + winTotal : null))
        postToParent('GAME_WIN', { amount: winTotal })
      }
      postToParent('ROUND_RESULT', { result, deal, win: winTotal })

      // Auto-reset to idle after 6s
      setTimeout(() => setRound(INITIAL_ROUND), 6000)
    })

    socket.on('bet:placed', ({ display_name, bet_type }) => {
      addActivity(`${display_name} bet on ${bet_type}`)
    })

    return () => {
      socket.off('connect')
      socket.off('connect_error')
      socket.off('disconnect')
      socket.off('connected')
      socket.off('round:start')
      socket.off('round:closed')
      socket.off('round:result')
      socket.off('bet:placed')
    }
  }, [playerId])

  // ── Place bet ──────────────────────────────────────────────
const placeBet = useCallback((betType: string, stake: number): Promise<{ balance: number }> => {
    return new Promise((resolve, reject) => {
      socketRef.current.emit(
        'bet:place',
        { round_id: round.roundId, bet_type: betType, stake },
        (res: { success?: boolean; balance?: number; error?: string }) => {
          if (res?.success) {
            const newBalance = res.balance ?? 0
            setBalance(newBalance)
            setRound(prev => ({
              ...prev,
              myBets: [
                ...prev.myBets,
                { betId: Math.random().toString(36).slice(2), betType, stake },
              ],
            }))
            postToParent('GAME_BALANCE_UPDATE', { balance: newBalance })
            resolve({ balance: newBalance })
          } else {
            reject(new Error(res?.error ?? 'Bet failed'))
          }
        }
      )
    })
  }, [round.roundId])

  return { connStatus, round, balance, error, activity, placeBet, initialResults, socket: socketRef.current }
}
